<template>
    <section
        class="m-blog-categories"
        :class="{'m-blog-categories--has-bg': hasCategories}"
    >
        <y-loading
            v-if="hasCategories && !categoriesData"
            :active="$wait.is('loading-categories-blog')"
            size="60"
            height="467px"
            position="relative"
        />
        <div
            v-else
            class="m-blog-categories--loaded"
        >
            <y-container>
                <div
                    v-if="hasCategories"
                    class="m-blog-categories__header"
                >
                    <div class="categories-header">
                        <div class="categories-header__title">
                            <h2>
                                {{ $t('blog.categories_title') }}
                            </h2>
                        </div>
                        <div class="categories-header__nav-icon">
                            <y-button
                                color="white"
                                :icon-append="`fa-caret-left`"
                                class="chevron-icons"
                                @click="categoryXScroll(-1)"
                            />

                            <y-button
                                color="white"
                                :icon-append="`fa-caret-right`"
                                class="chevron-icons"
                                @click="categoryXScroll(1)"
                            />
                        </div>
                    </div>
                </div>
                <div
                    v-else-if="postsToShow.length"
                    class="author-articles-title"
                >
                    <h2>
                        {{ $t('blog.latest_articles') }}
                    </h2>
                </div>
                <ul
                    v-if="hasCategories"
                    ref="blogCategory"
                    class="m-blog-categories__categories"
                >
                    <li
                        v-for="tag in categoriesData"
                        :key="tag.id"
                        class="blog-category"
                        :class="{'blog-category--active': categoryUrlEncode === tag.url_encode}"
                        @click="setCategoryUrlToShow(tag.url_encode)"
                    >
                        <div class="blog-category__icon">
                            <img
                                :src="imgSrc(tag.image)"
                                :alt="tag.title"
                            >
                        </div>
                        <div class="blog-category__title">
                            <h2>
                                {{ tag.title }}
                            </h2>
                        </div>
                    </li>
                </ul>
                <y-blog-wrapper
                    :category-url-encode="categoryUrlEncode"
                    :loading-more-btn="loadingMoreBtn"
                    :show-more-btn="postsData.get(categoryUrlEncode).show_more_btn"
                    :posts-to-show="postsToShow"
                    @fetchPosts="fetchPosts(categoryUrlEncode)"
                />
            </y-container>
        </div>
    </section>
</template>

<script >
    import YBlogWrapper from '@modules/blog/components/Categories/BlogWrapper/BlogWrapper.vue';

    export default {
        name      : 'BlogHero',
        components: {
            YBlogWrapper,
        },

        props: {
            hasCategories: {
                type: Boolean,
            },
        },

        /**
         *
         * @inheritDoc
         */
        data() {
            return {
                categoriesData   : null,
                postsData        : new Map(),
                page             : 0,
                categoryUrlEncode: null,
                postsToShow      : [],
            };
        },

        computed: {

            /**
             * Return number of posts in each row base on device width
             *
             * @returns {number}
             */
            postsPerRow() {
                // eslint-disable-next-line no-nested-ternary
                return this.$mq === 'xlg' ? 3
                    : this.$mq === 'lg' ? 2
                        : 1;
            },

            /**
             * Loading see-more btn
             *
             * @returns {boolean}
             */
            loadingMoreBtn() {
                return this.$wait.is(`loading-posts-blog-${this.categoryUrlEncode}`);
            },
        },

        watch: {
            categoryUrlEncode: {
                /**
                 * Category changes
                 *
                 * @param {string} newCategoryUrlEncode
                 * @param {string} preVal
                 */
                handler(newCategoryUrlEncode, preVal) {
                    if (newCategoryUrlEncode !== preVal) {
                        this.setPostToShow(newCategoryUrlEncode);
                    }
                },
                immediate: false,
            },

        },

        /**
         *
         * @inheritDoc
         */
        created() {
            if (this.hasCategories) {
                this.fetch();
            } else {
                this.postsData.set('latest', {
                    items               : [],
                    last_page           : 1,
                    current_page        : 0,
                    numbersRequestToBack: 0,
                });
                this.setCategoryUrlToShow('latest');
            }
        },

        /**
         *
         * @inheritDoc
         */
        methods: {
            /**
             * Set Posts to show
             *
             * @param {string} categoryUrlEncode
             */
            setPostToShow(categoryUrlEncode) {
                if (categoryUrlEncode === this.categoryUrlEncode) {
                    this.postsToShow = this.postsData.get(categoryUrlEncode).items;
                }
                if (!this.postsData.get(categoryUrlEncode).items.length) {
                    this.fetchPosts(categoryUrlEncode);
                }
            },

            /**
             * Set Posts Data
             * Set Items and last page and current page
             *
             * @param {string} categoryUrlEncode
             * @param {object} response
             */
            setPostsData(categoryUrlEncode, response) {
                this.postsData.get(categoryUrlEncode).items = this.postsData.get(categoryUrlEncode).items.concat(response.data.results);
                this.postsData.get(categoryUrlEncode).last_page = response.data.metadata.last_page;
                this.postsData.get(categoryUrlEncode).current_page = response.data.metadata.current_page;
                this.postsData.get(categoryUrlEncode).show_more_btn = this.postsData.get(categoryUrlEncode).last_page > this.postsData.get(categoryUrlEncode).current_page;
                this.setPostToShow(categoryUrlEncode);
            },

            /**
             * Scroll left or right [direction:1 right, direction:-1 left]
             *
             * @param {number} direction
             */
            categoryXScroll(direction) {
                const containerWidth = this.$refs.blogCategory.offsetWidth;
                let scrollValue = 0.7;
                if (this.postsPerRow !== 1) {
                    scrollValue = 0.25;
                }

                this.$refs.blogCategory.scrollBy({
                    left    : direction * containerWidth * scrollValue,
                    behavior: 'smooth',
                });
            },

            /**
             *
             *@param {string} urlEncode
             */
            setCategoryUrlToShow(urlEncode) {
                this.categoryUrlEncode = urlEncode;
            },

            /**
             * Check whether the user has request or not
             *
             * @param {string} categoryUrlEncode
             * @returns {boolean}
             */
            hasRequestToBack(categoryUrlEncode) {
                this.postsData.get(categoryUrlEncode).numbersRequestToBack += 1;
                return this.postsData.get(categoryUrlEncode).numbersRequestToBack > 1;
            },

            /**
             * Reset the number of request to zero
             * Now user can request again
             *
             * @param {string} categoryUrlEncode
             */
            resetNumberOfRequest(categoryUrlEncode) {
                this.postsData.get(categoryUrlEncode).numbersRequestToBack = 0;
            },

            /**
             * fetch another category
             *
             * @param {string} categoryUrlEncode
             */
            fetchPosts(categoryUrlEncode) {
                if (this.hasRequestToBack(categoryUrlEncode)) {
                    return;
                }

                this.$wait.start(`loading-posts-blog-${categoryUrlEncode}`);

                let params;
                if (categoryUrlEncode === 'latest') {
                    params = {
                        page     : this.postsData.get(categoryUrlEncode).current_page + 1,
                        paginated: 1,
                        per_page : 6,
                    };
                } else {
                    params = {
                        tagging  : categoryUrlEncode,
                        page     : this.postsData.get(categoryUrlEncode).current_page + 1,
                        paginated: 1,
                        per_page : 3,
                    };
                }

                this.$services.Content.postList({
                    type     : 'blog',
                    including: ['title', 'title2', 'created_at', 'publisher', 'published_at', 'image', 'url_encode', 'view_count', 'tagging_cats', 'user', 'custom', 'slug'],
                    locale   : this.$i18n.locale,
                    ...params,
                })
                    .then((response) => {
                        this.setPostsData(categoryUrlEncode, response);
                    })
                    .catch((error) => {
                        this.handleError(error);
                    })
                    .finally(() => {
                        this.$wait.end(`loading-posts-blog-${categoryUrlEncode}`);
                        this.resetNumberOfRequest(categoryUrlEncode);
                    });
            },

            /**
             * Check the Promise.all Request Error
             *
             * @param {Array} req - Responses array
             * @param {index} index - number of response
             * @returns {boolean}
             */
            handleReq(req, index) {
                return req && req[index] && !req[index].isAxiosError;
            },

            /**
             * fetch hero data
             *
             * @returns {Promise}
             */
            fetch() {
                this.$wait.start('loading-categories-blog');
                const category = this.$services.Tagging.labels({
                    slug     : 'blog-category',
                    including: ['id', 'image', 'title', 'slug', 'url_encode', 'tagging_tags'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                return Promise.all([category])
                    .then((responses) => {
                        if (this.handleReq(responses, 0)) {
                            this.categoriesData = responses[0].data.results;
                            this.categoriesData.forEach((c) => {
                                this.postsData.set(c.url_encode, {
                                    items               : [],
                                    last_page           : 1,
                                    current_page        : 0,
                                    numbersRequestToBack: 0,
                                });
                            });
                            this.setCategoryUrlToShow(this.categoriesData[0].url_encode);
                        }
                    })
                    .catch((error) => {
                        this.handleError(error);
                    })
                    .finally(() => {
                        this.$wait.end('loading-categories-blog');
                    });
            },
        },
    };

</script>

<style lang="scss" src="./categories.scss"/>
